import React, { useState, useMemo } from 'react'
import {
  ChevronDown,
  Calendar,
  X,
  ArrowLeft,
  Eye,
  ExternalLink,
} from 'lucide-react'
import styled, { css } from 'styled-components'

const topicsData = [
  {
    name: 'Transit Pass Enrollment',
    conversations: 1324,
    score: 0.62,
    description:
      "Includes issues related to enrolling in transit pass programs, activating new cards, replacing expired or lost/stolen cards, reporting cards that aren't working, tracking delayed delivery, and resolving virtual card issues.",
  },
  {
    name: 'Parking Reimbursement',
    conversations: 1020,
    score: 0.58,
    description:
      'Covers questions about parking reimbursement eligibility, submission processes, approval timelines, payment methods, and resolving reimbursement discrepancies or delays.',
  },
  {
    name: 'Lost or Stolen Fleet Cards',
    conversations: 875,
    score: 0.53,
    description:
      'Handles reports of lost or stolen Fleet cards, card replacement procedures, temporary card access, fraud prevention, and securing accounts after card loss.',
  },
  {
    name: 'Pre-Tax Eligibility',
    conversations: 648,
    score: 0.67,
    description:
      'Addresses questions about pre-tax benefit eligibility, enrollment requirements, contribution limits, tax savings calculations, and changes to pre-tax elections.',
  },
  {
    name: 'Benefit Enrollment Errors',
    conversations: 522,
    score: 0.49,
    description:
      'Resolves issues with benefit enrollment errors, incorrect deductions, failed enrollments, updating enrollment information, and correcting enrollment mistakes.',
  },
  {
    name: 'Stipend Questions',
    conversations: 416,
    score: 0.73,
    description:
      'Answers questions about commuter stipend amounts, eligibility criteria, payment schedules, stipend usage restrictions, and how to claim or track stipend benefits.',
  },
  {
    name: 'Micromobility Coverage',
    conversations: 298,
    score: 0.75,
    description:
      'Covers questions about micromobility options like bikes, scooters, and e-bikes, including coverage eligibility, reimbursement processes, and supported providers.',
  },
  {
    name: 'Program Launch Timelines',
    conversations: 236,
    score: 0.71,
    description:
      'Provides information about new program launch dates, rollout schedules, implementation phases, and when employees can expect access to new benefits.',
  },
  {
    name: 'Transit Agencies Sync',
    conversations: 218,
    score: 0.68,
    description:
      'Addresses issues with transit agency integrations, card syncing problems, agency account linking, and resolving discrepancies between Fleet and transit agency systems.',
  },
  {
    name: 'General FAQs',
    conversations: 192,
    score: 0.79,
    description:
      'Handles general questions about Fleet programs, account management, benefit options, program policies, and other miscellaneous inquiries not covered by specific categories.',
  },
]

const subtopicsData: Record<string, any[]> = {
  'Transit Pass Enrollment': [
    { name: 'Activating a new card', conversations: 263, score: 0.553 },
    { name: 'Lost or stolen cards', conversations: 237, score: 0.483 },
    { name: 'Card not working', conversations: 124, score: 0.564 },
    { name: 'Replacing expired card', conversations: 76, score: 0.514 },
    { name: 'Delayed card delivery', conversations: 58, score: 0.472 },
    { name: 'Virtual card issues', conversations: 41, score: 0.652 },
  ],
}

const conversationsData = [
  {
    id: 1,
    name: 'Alex Turner',
    preview: "I activated my new card yesterday, but it's still not working",
    timestamp: 'yesterday',
    fullConversation: [
      {
        speaker: 'Alex Turner',
        message:
          "I activated my new card yesterday, but it's still not working",
      },
      {
        speaker: 'Fleet AI Concierge',
        message:
          'I understand your frustration. After activation, it can take up to 24 hours for the card to be fully active in the system. Please try again in a few hours.',
      },
    ],
    cxRating: 2,
    cxNote:
      'Customer frustrated with activation delay. Clear timeline provided.',
  },
  {
    id: 2,
    name: 'Jim Dietrich',
    preview: 'I just received my new card in the mail. How do I activate it?',
    timestamp: '1m',
    fullConversation: [
      {
        speaker: 'Jim Dietrich',
        message:
          'I just received my new card in the mail. How do I activate it?',
      },
      {
        speaker: 'Fleet AI Concierge',
        message:
          "Great to hear your new card has arrived. Here's how to activate your card:\n1. Log in to your account via the app or website.\n2. Go to the 'Cards' section.\n3. Tap on 'Activate New Card' and follow the prompts.\n4. You'll receive a confirmation once it's active.",
      },
    ],
    cxRating: 3,
    cxNote:
      'The customer asked how to activate a new card. Fin provided clear instructions but the customer was not able to find steps in product. As a result, the customer became frustrated with the experience.',
  },
  {
    id: 3,
    name: 'Kay Brakus',
    preview: "I didn't get a prompt to activate my card. Where can I find it?",
    timestamp: '2h',
    fullConversation: [
      {
        speaker: 'Kay Brakus',
        message:
          "I didn't get a prompt to activate my card. Where can I find it?",
      },
      {
        speaker: 'Fleet AI Concierge',
        message:
          'You can find the activation option in your account settings under the Cards section. Look for the "Activate Card" button.',
      },
    ],
    cxRating: 4,
    cxNote: 'Customer found the activation process after guidance.',
  },
  {
    id: 4,
    name: 'Shelley Mraz',
    preview: 'How long does it take for my card to be ready after activation?',
    timestamp: '5h',
    fullConversation: [
      {
        speaker: 'Shelley Mraz',
        message:
          'How long does it take for my card to be ready after activation?',
      },
      {
        speaker: 'Fleet AI Concierge',
        message:
          "Your card should be ready to use within 24 hours of activation. You'll receive a confirmation email once it's active.",
      },
    ],
    cxRating: 5,
    cxNote: 'Quick and helpful response.',
  },
]

const generateSparklineData = (baseValue: number) => {
  return Array.from({ length: 7 }, (_, i) => ({
    week: i + 1,
    conversations: Math.floor(baseValue / 7 + Math.random() * (baseValue / 5)),
    score: Math.floor(50 + Math.random() * 40),
  }))
}

const MainContent = styled.main`
  flex: 1;
  overflow: auto;
`

const ContentContainer = styled.div`
  padding: 2rem;
`

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const HeaderText = styled.div``

const PageTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`

const PageDescription = styled.p`
  font-size: 1rem;
  color: #4b5563;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`

const ButtonText = styled.span`
  font-size: 0.875rem;
  color: #374151;
`

const IndicatorDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: #bfa6cc;
`

const MainGrid = styled.div`
  display: flex;
  gap: 1.5rem;
`

const TopicsGridContainer = styled.div`
  flex: 1;
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  overflow: visible;
`

const TopicsTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 1rem;
`

const TopicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  grid-auto-rows: 1fr;
  overflow: visible;
`

const TopicTile = styled.button<{
  $colSpan: number
  $rowSpan: number
  $backgroundColor: string
  $opacity: number
}>`
  grid-column: span ${(props) => props.$colSpan};
  grid-row: span ${(props) => props.$rowSpan};
  padding: 1.5rem;
  border-radius: 0.75rem;
  transition: all 0.3s;
  position: relative;
  overflow: visible;
  border: none;
  cursor: pointer;
  background-color: ${(props) => props.$backgroundColor};
  opacity: ${(props) => props.$opacity};

  &:hover {
    transform: scale(1.02);
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
`

const TileContent = styled.div`
  position: relative;
  z-index: 10;
`

const TileTitle = styled.h4`
  text-align: left;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: white;
`

const TileText = styled.p`
  text-align: left;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
`

const Tooltip = styled.div`
  position: absolute;
  left: 100%;
  margin-left: 1rem;
  top: 0;
  width: 20rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 1rem;
  z-index: 9999;
  pointer-events: none;
`

const TooltipTitle = styled.h4`
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  color: #111827;
`

const TooltipStats = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const TooltipStat = styled.div`
  font-weight: 500;
`

const TooltipDescription = styled.p`
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.625;
`

const TooltipArrow = styled.div`
  position: absolute;
  right: 100%;
  top: 1.5rem;
  width: 0;
  height: 0;
  border-top: 0.5rem solid transparent;
  border-bottom: 0.5rem solid transparent;
  border-right: 0.5rem solid white;
`

const TooltipArrowBorder = styled.div`
  position: absolute;
  right: 100%;
  top: 1.5rem;
  margin-right: 1px;
  width: 0;
  height: 0;
  border-top: 0.5rem solid transparent;
  border-bottom: 0.5rem solid transparent;
  border-right: 0.5rem solid #e5e7eb;
`

const SparklinesContainer = styled.div`
  width: 20rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const SparklineWrapper = styled.div`
  position: relative;
  cursor: pointer;
`

const SparklineLabel = styled.div`
  font-size: 0.75rem;
  color: #4b5563;
  margin-bottom: 0.25rem;
`

const SparklineSVG = styled.svg`
  overflow: visible;
`

const SparklineTooltip = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-100%);
  margin-bottom: 0.5rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  white-space: nowrap;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  z-index: 20;
  pointer-events: none;
`

const SparklineTooltipTitle = styled.div`
  font-weight: 600;
  color: #111827;
`

const SparklineTooltipText = styled.div`
  color: #4b5563;
`

const DrillDownContainer = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4b5563;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  margin-bottom: 1rem;

  &:hover {
    color: #111827;
  }
`

const DrillDownTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.5rem;
`

const SubtopicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SubtopicCard = styled.button<{ $backgroundColor: string }>`
  padding: 1.5rem;
  border-radius: 0.75rem;
  text-align: left;
  transition: all 0.2s;
  border: none;
  cursor: pointer;
  background-color: ${(props) => props.$backgroundColor};

  &:hover {
    transform: scale(1.02);
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`

const SubtopicTitle = styled.h4`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: white;
  font-size: 1.125rem;
`

const SubtopicText = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
`

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const Drawer = styled.div`
  background-color: white;
  width: 100%;
  max-width: 56rem;
  height: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
`

const DrawerHeader = styled.div`
  border-bottom: 1px solid #e5e7eb;
  padding: 1.5rem;
`

const DrawerHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const DrawerTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
`

const DrawerCloseButton = styled.button`
  padding: 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }
`

const DrawerStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: #4b5563;
`

const DrawerStat = styled.div``

const DrawerStatValue = styled.span`
  font-weight: 600;
`

const DrawerContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
`

const ConversationsList = styled.div`
  width: 24rem;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  background-color: #f9fafb;
`

const ConversationsListContent = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ConversationsCount = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 1rem;
`

const ConversationItem = styled.button<{ $selected: boolean }>`
  width: 100%;
  padding: 1rem;
  border-radius: 0.5rem;
  text-align: left;
  transition: background-color 0.2s;
  border: 1px solid;
  cursor: pointer;

  ${(props) =>
    props.$selected
      ? css`
          background-color: rgba(191, 166, 204, 0.2);
          border-color: #bfa6cc;
        `
      : css`
          background-color: white;
          border-color: #e5e7eb;

          &:hover {
            background-color: #f3f4f6;
          }
        `}
`

const ConversationItemContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`

const Avatar = styled.div<{ $color: string }>`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background-color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const AvatarText = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
`

const ConversationDetails = styled.div`
  flex: 1;
  min-width: 0;
`

const ConversationName = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  color: #111827;
`

const ConversationPreview = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ConversationTimestamp = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`

const ConversationDetail = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  background-color: white;
`

const ConversationDetailContent = styled.div`
  max-width: 48rem;
  margin: 0 auto;
`

const ConversationDetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const ConversationDetailTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`

const ConversationDetailActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const IconButton = styled.button`
  padding: 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }
`

const CXRatingBox = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 2px solid #bfa6cc;
  background: linear-gradient(
    to bottom right,
    rgba(191, 166, 204, 0.1),
    rgba(92, 22, 49, 0.05)
  );
`

const CXRatingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const CXRatingTag = styled.div`
  padding: 0.25rem 0.5rem;
  background-color: #111827;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
`

const CXNote = styled.p`
  font-size: 0.875rem;
  color: #374151;
`

const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const MessageBubble = styled.div<{ $isAI: boolean }>`
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid;

  ${(props) =>
    props.$isAI
      ? css`
          background-color: #f9fafb;
          border-color: #e5e7eb;
        `
      : css`
          background-color: rgba(191, 166, 204, 0.1);
          border-color: rgba(191, 166, 204, 0.3);
        `}
`

const MessageContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`

const MessageAvatar = styled.div<{ $isAI: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${(props) => (props.$isAI ? '#5C1631' : '#BFA6CC')};
`

const MessageText = styled.div`
  flex: 1;
`

const MessageSpeaker = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  color: #111827;
`

const MessageBody = styled.div`
  font-size: 0.875rem;
  color: #374151;
  white-space: pre-line;
`

const MessageTimestamp = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
`

const TopicsExplorer = React.memo(() => {
  const [drillDownTopic, setDrillDownTopic] = useState<string | null>(null)
  const [conversationsDrawer, setConversationsDrawer] = useState<{
    topic: string
    day: any
  } | null>(null)
  const [selectedConversation, setSelectedConversation] = useState<any>(
    conversationsData[1]
  )
  const [hoveredTile, setHoveredTile] = useState<string | null>(null)
  const [hoveredSparkline, setHoveredSparkline] = useState<{
    topic: string
    data: any
  } | null>(null)

  const sparklineDataMap = useMemo(() => {
    const map: Record<string, any[]> = {}
    topicsData.forEach((topic) => {
      map[topic.name] = generateSparklineData(topic.conversations)
    })
    return map
  }, [])

  const getCardColor = (conversations: number) => {
    const maxConversations = Math.max(...topicsData.map((t) => t.conversations))
    const ratio = conversations / maxConversations

    // Gradient from dark maroon (high volume) to light lavender (low volume)
    if (ratio > 0.8) return '#57112C' // Darkest maroon
    if (ratio > 0.6) return '#6B2842' // Dark purple
    if (ratio > 0.4) return '#8B5A7D' // Medium purple
    if (ratio > 0.2) return '#A67B99' // Light purple
    return '#BFA6CC' // Lightest lavender
  }

  const getScoreColor = (score: number) => {
    if (score >= 0.7) return '#BFA6CC'
    if (score >= 0.5) return '#7C4C78'
    return '#5C1631'
  }

  const maxConversations = Math.max(...topicsData.map((t) => t.conversations))
  const getTileSize = (conversations: number) => {
    const ratio = conversations / maxConversations
    if (ratio > 0.7) return 'large'
    if (ratio > 0.4) return 'medium'
    return 'small'
  }

  return (
    <div>
      <MainContent>
        <ContentContainer>
          {!drillDownTopic ? (
            <>
              <HeaderSection>
                <HeaderText>
                  <PageTitle>Topics Explorer</PageTitle>
                  <PageDescription>
                    Explore the most common topics and conversations handled by
                    Fleet AI Concierge.
                  </PageDescription>
                </HeaderText>
                <HeaderActions>
                  <HeaderButton>
                    <Calendar
                      style={{
                        width: '1rem',
                        height: '1rem',
                        color: '#4b5563',
                      }}
                    />
                    <ButtonText>Nov 20, 2024 - Feb 11, 2025</ButtonText>
                  </HeaderButton>
                  <HeaderButton>
                    <IndicatorDot />
                    <ButtonText>Employee Experience (EX) Score</ButtonText>
                    <ChevronDown
                      style={{
                        width: '1rem',
                        height: '1rem',
                        color: '#4b5563',
                      }}
                    />
                  </HeaderButton>
                  <HeaderButton>
                    <ButtonText>View 10</ButtonText>
                    <ChevronDown
                      style={{
                        width: '1rem',
                        height: '1rem',
                        color: '#4b5563',
                      }}
                    />
                  </HeaderButton>
                </HeaderActions>
              </HeaderSection>

              <MainGrid>
                <TopicsGridContainer>
                  <TopicsTitle>10 Highest-volume topics</TopicsTitle>
                  <TopicsGrid>
                    {topicsData.map((topic) => {
                      const size = getTileSize(topic.conversations)
                      const colSpan = size === 'large' ? 2 : 1
                      const rowSpan =
                        size === 'large' ? 2 : size === 'medium' ? 2 : 1

                      return (
                        <TopicTile
                          key={topic.name}
                          onClick={() => setDrillDownTopic(topic.name)}
                          onMouseEnter={() => setHoveredTile(topic.name)}
                          onMouseLeave={() => setHoveredTile(null)}
                          $colSpan={colSpan}
                          $rowSpan={rowSpan}
                          $backgroundColor={getCardColor(topic.conversations)}
                          $opacity={hoveredTile === topic.name ? 1 : 0.9}
                        >
                          <TileContent>
                            <TileTitle>{topic.name}</TileTitle>
                            <TileText>
                              {topic.conversations.toLocaleString()}{' '}
                              CONVERSATIONS
                            </TileText>
                            <TileText>
                              {Math.round(topic.score * 100)}% EX SCORE
                            </TileText>
                          </TileContent>
                          {hoveredTile === topic.name && (
                            <Tooltip>
                              <TooltipTitle>{topic.name}</TooltipTitle>
                              <TooltipStats>
                                <TooltipStat>
                                  {topic.conversations.toLocaleString()}{' '}
                                  CONVERSATIONS
                                </TooltipStat>
                                <TooltipStat>
                                  {Math.round(topic.score * 100)}% EX SCORE
                                </TooltipStat>
                              </TooltipStats>
                              <TooltipDescription>
                                {topic.description}
                              </TooltipDescription>
                              <TooltipArrow />
                              <TooltipArrowBorder />
                            </Tooltip>
                          )}
                        </TopicTile>
                      )
                    })}
                  </TopicsGrid>
                </TopicsGridContainer>

                <SparklinesContainer>
                  {topicsData.map((topic) => {
                    const sparklineData = sparklineDataMap[topic.name]
                    const maxValue = Math.max(
                      ...sparklineData.map((d) => d.conversations)
                    )

                    return (
                      <SparklineWrapper
                        key={topic.name}
                        onMouseMove={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect()
                          const x = e.clientX - rect.left
                          const index = Math.floor(
                            (x / rect.width) * sparklineData.length
                          )
                          if (index >= 0 && index < sparklineData.length) {
                            setHoveredSparkline({
                              topic: topic.name,
                              data: sparklineData[index],
                            })
                          }
                        }}
                        onMouseLeave={() => setHoveredSparkline(null)}
                      >
                        <SparklineLabel>{topic.name}</SparklineLabel>
                        <SparklineSVG
                          width='100%'
                          height='40'
                          viewBox='0 0 320 40'
                          preserveAspectRatio='none'
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect()
                            const x = e.clientX - rect.left
                            const index = Math.floor(
                              (x / rect.width) * sparklineData.length
                            )
                            if (index >= 0 && index < sparklineData.length) {
                              setConversationsDrawer({
                                topic: topic.name,
                                day: sparklineData[index],
                              })
                            }
                          }}
                        >
                          <path
                            d={`M 0 40 ${sparklineData
                              .map((d, i) => {
                                const x = (i / (sparklineData.length - 1)) * 320
                                const y = 40 - (d.conversations / maxValue) * 30
                                return `L ${x} ${y}`
                              })
                              .join(' ')} L 320 40 Z`}
                            fill='#BFA6CC'
                            opacity='0.3'
                          />
                          <path
                            d={`M ${sparklineData
                              .map((d, i) => {
                                const x = (i / (sparklineData.length - 1)) * 320
                                const y = 40 - (d.conversations / maxValue) * 30
                                return `${x} ${y}`
                              })
                              .join(' L ')}`}
                            stroke='#5C1631'
                            strokeWidth='2'
                            fill='none'
                          />
                          {hoveredSparkline?.topic === topic.name && (
                            <line
                              x1={
                                (hoveredSparkline.data.week /
                                  sparklineData.length) *
                                320
                              }
                              y1='0'
                              x2={
                                (hoveredSparkline.data.week /
                                  sparklineData.length) *
                                320
                              }
                              y2='40'
                              stroke='#5C1631'
                              strokeWidth='1'
                              strokeDasharray='2,2'
                            />
                          )}
                        </SparklineSVG>
                        {hoveredSparkline?.topic === topic.name && (
                          <SparklineTooltip>
                            <SparklineTooltipTitle>
                              MAY {hoveredSparkline.data.week + 6}
                            </SparklineTooltipTitle>
                            <SparklineTooltipText>
                              {hoveredSparkline.data.conversations}{' '}
                              CONVERSATIONS
                            </SparklineTooltipText>
                            <SparklineTooltipText>
                              {hoveredSparkline.data.score}% EX SCORE
                            </SparklineTooltipText>
                          </SparklineTooltip>
                        )}
                      </SparklineWrapper>
                    )
                  })}
                </SparklinesContainer>
              </MainGrid>
            </>
          ) : (
            <DrillDownContainer>
              <BackButton onClick={() => setDrillDownTopic(null)}>
                <ArrowLeft style={{ width: '1rem', height: '1rem' }} />
                Back to 10 Highest-volume topics
              </BackButton>
              <DrillDownTitle>{drillDownTopic}</DrillDownTitle>

              {subtopicsData[drillDownTopic] && (
                <SubtopicsGrid>
                  {subtopicsData[drillDownTopic].map((subtopic) => (
                    <SubtopicCard
                      key={subtopic.name}
                      $backgroundColor={getScoreColor(subtopic.score)}
                    >
                      <SubtopicTitle>{subtopic.name}</SubtopicTitle>
                      <SubtopicText>
                        {subtopic.conversations} CONVERSATIONS
                      </SubtopicText>
                      <SubtopicText>
                        {Math.round(subtopic.score * 100)}% EX SCORE
                      </SubtopicText>
                    </SubtopicCard>
                  ))}
                </SubtopicsGrid>
              )}
            </DrillDownContainer>
          )}
        </ContentContainer>
      </MainContent>

      {conversationsDrawer && (
        <DrawerOverlay onClick={() => setConversationsDrawer(null)}>
          <Drawer onClick={(e) => e.stopPropagation()}>
            <DrawerHeader>
              <DrawerHeaderTop>
                <DrawerTitle>
                  {conversationsDrawer.topic} - MAY{' '}
                  {conversationsDrawer.day.week + 6}
                </DrawerTitle>
                <DrawerCloseButton onClick={() => setConversationsDrawer(null)}>
                  <X
                    style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      color: '#4b5563',
                    }}
                  />
                </DrawerCloseButton>
              </DrawerHeaderTop>
              <DrawerStats>
                <DrawerStat>
                  <DrawerStatValue>
                    {conversationsDrawer.day.conversations}
                  </DrawerStatValue>{' '}
                  conversations
                </DrawerStat>
                <DrawerStat>
                  <DrawerStatValue>
                    {conversationsDrawer.day.score}%
                  </DrawerStatValue>{' '}
                  EX Score
                </DrawerStat>
              </DrawerStats>
            </DrawerHeader>

            <DrawerContent>
              <ConversationsList>
                <ConversationsListContent>
                  <ConversationsCount>
                    {conversationsData.length} conversations
                  </ConversationsCount>
                  {conversationsData.map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      onClick={() => setSelectedConversation(conv)}
                      $selected={selectedConversation?.id === conv.id}
                    >
                      <ConversationItemContent>
                        <Avatar $color='#BFA6CC'>
                          <AvatarText>{conv.name[0]}</AvatarText>
                        </Avatar>
                        <ConversationDetails>
                          <ConversationName>{conv.name}</ConversationName>
                          <ConversationPreview>
                            {conv.preview}
                          </ConversationPreview>
                          <ConversationTimestamp>
                            {conv.timestamp}
                          </ConversationTimestamp>
                        </ConversationDetails>
                      </ConversationItemContent>
                    </ConversationItem>
                  ))}
                </ConversationsListContent>
              </ConversationsList>

              {selectedConversation && (
                <ConversationDetail>
                  <ConversationDetailContent>
                    <ConversationDetailHeader>
                      <ConversationDetailTitle>
                        {selectedConversation.name}
                      </ConversationDetailTitle>
                      <ConversationDetailActions>
                        <IconButton>
                          <Eye
                            style={{
                              width: '1rem',
                              height: '1rem',
                              color: '#4b5563',
                            }}
                          />
                        </IconButton>
                        <IconButton>
                          <ExternalLink
                            style={{
                              width: '1rem',
                              height: '1rem',
                              color: '#4b5563',
                            }}
                          />
                        </IconButton>
                      </ConversationDetailActions>
                    </ConversationDetailHeader>

                    <CXRatingBox>
                      <CXRatingBadge>
                        <CXRatingTag>
                          CX rating: {selectedConversation.cxRating}
                        </CXRatingTag>
                      </CXRatingBadge>
                      <CXNote>{selectedConversation.cxNote}</CXNote>
                    </CXRatingBox>

                    <MessagesList>
                      {selectedConversation.fullConversation.map(
                        (msg: any, index: number) => (
                          <MessageBubble
                            key={index}
                            $isAI={msg.speaker === 'Fleet AI Concierge'}
                          >
                            <MessageContent>
                              <MessageAvatar
                                $isAI={msg.speaker === 'Fleet AI Concierge'}
                              >
                                <AvatarText>{msg.speaker[0]}</AvatarText>
                              </MessageAvatar>
                              <MessageText>
                                <MessageSpeaker>{msg.speaker}</MessageSpeaker>
                                <MessageBody>{msg.message}</MessageBody>
                                {msg.speaker !== 'Fleet AI Concierge' && (
                                  <MessageTimestamp>
                                    {selectedConversation.timestamp}
                                  </MessageTimestamp>
                                )}
                              </MessageText>
                            </MessageContent>
                          </MessageBubble>
                        )
                      )}
                    </MessagesList>
                  </ConversationDetailContent>
                </ConversationDetail>
              )}
            </DrawerContent>
          </Drawer>
        </DrawerOverlay>
      )}
    </div>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  TopicsExplorer.displayName = 'TopicsExplorer'
}

export default TopicsExplorer
